import { Style } from 'inlines'
import { Title } from '@app/components/core/typography/Title'
import { Follower } from '@app/types/Follower'
import { AvatarStack } from '../avatarStack/AvatarStack'
import Column from '@app/components/layout/Column'
import { RegionStatusRow } from './blocks/RegionStatusRow'
import { Image } from '@app/components/core/image/Image'
import { LinkWrapper } from '@app/components/core/link/LinkWrapper'
import defaultHousingFormPicture from '@app/assets/defaultHousingFormPicture.png'
import { expandImage, type StoredImage } from '@app/types/StoredImage'

export interface HousingFormCardProps {
  image: string | StoredImage
  title: string
  followers: Array<Follower>
  followerCount: number
  initiativeCount?: number
  style?: Style
  to: string
}

export const HousingFormCard = ({
  image,
  title,
  followers,
  followerCount,
  initiativeCount,
  style,
  to,
}: HousingFormCardProps) => {
  const { src, altText } = expandImage(image, title)
  return (
    <LinkWrapper
      to={to}
      data-testid="housing-form-card"
      style={{
        position: 'relative',
        backgroundColor: 'var(--palette-white)',
        borderRadius: '1.25rem',
        overflow: 'hidden',
        cursor: 'pointer',
        width: '100%',
        height: '20rem',
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        ...style,
      }}
    >
      {/* Main Image */}
      <Image
        src={src || defaultHousingFormPicture}
        alt={altText}
        resizeWidth={800}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />

      {/* Overlay with Title and Profile Icons */}
      <Column
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background:
            'linear-gradient(180deg, rgba(45, 31, 21, 0.00) 0%, #28220D 100%)',
          padding: '1.5rem',
        }}
      >
        {/* Title */}
        <Title.H3
          style={{
            color: 'var(--palette-white)',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            margin: 0,
          }}
        >
          {title}
        </Title.H3>

        {initiativeCount ? (
          <RegionStatusRow initiatives={initiativeCount} version="light" />
        ) : null}

        {/* Profile Icons and Follower Count */}
        <AvatarStack
          size={'extra-small'}
          avatars={followers}
          total={followerCount}
          version="light"
          style={{
            marginTop: '0.5rem',
          }}
        />
      </Column>
    </LinkWrapper>
  )
}
